import { Signal } from '@angular/core';
import { Canvas, Point } from 'fabric';
import { Hotspot, HotspotType, IDisplaySlide } from '@pcp/core/interfaces';
import { isDebugEnabled } from 'shared/util-runtime-config';
import { PointerContext, SlideCapability, SlideHostContext } from '../slide-capability';

/**
 * Hotspot types the slide renderer hit-tests (parity with `svg-canvas.component.ts:646-651`).
 *
 * ⚠️ WIRE-FORMAT INVARIANT (D6/C14): the synced deactivated-set keys hotspots by their INDEX into
 * `slide.hotspots.filter(this set)`. That index is only stable across clients while every connected
 * client filters with an IDENTICAL set — so changing the members here is a breaking change that must
 * ship to all client roles (teacher + beamer) simultaneously. In a mixed-version classroom a differing
 * set would silently dim the wrong hotspot on the lagging client. The durable fix (if ever needed) is a
 * stable `Hotspot.id` in `@pcp/core/interfaces` so the set keys on identity, not position.
 */
const SUPPORTED_TYPES: ReadonlySet<HotspotType> = new Set([
    HotspotType.forward,
    HotspotType.integrated,
    HotspotType.nested,
    HotspotType.overlay,
    HotspotType.moodcheck,
]);

/**
 * HotspotLayer — geometry-only interactive hotspots, as a {@link SlideCapability}
 * (`canvas-renderer-composition`).
 *
 * Hotspots are invisible: it contributes NO fabric objects (so `contributeObjects` is omitted) and
 * hit-tests pointer events the host dispatches (`claims` → cursor/activate). Coordinates are logical
 * (scene) space — anchored to the image, so they pan with a panorama. The host owns the canvas
 * subscriptions and the pointer claim-chain (Decision C12); this capability only declares whether it
 * claims a point and what to do on a claimed down. Interactivity is gated by the shared interaction
 * context — no freehand/selection tool owns the pointer AND `disableElementUsage` is not set — so a
 * drawing gesture never activates a hotspot and follower apps cannot activate.
 *
 * Deactivation is SYNCED, teacher-authoritative display-state (Decision C14 / D6). This capability
 * owns NO deactivation state: it PROJECTS the passed-down `deactivated` index set (dim + skip
 * hit-test) and, when the writer activates a non-`forward` hotspot, emits `onDeactivate(index)` UP so
 * the host writes it to the Classroom Y.Doc — which flows back down through the same set (the writer
 * sees its own deactivation via the synced signal, not a local mutation). `forward` hotspots stay
 * active for back-navigation. The set is re-armed to empty by the Y.Doc `setSlide` / `removeSlide` /
 * `resetSlideToInitial` cascades, so it never survives a re-show.
 *
 * The debug overlay (Ctrl+Shift+D, unified `config.debug`) is painted on the 2D context via
 * {@link onAfterRender} (the host's single `after:render` pass), in scene coordinates so it tracks the
 * viewport transform.
 */
/** A hotspot rectangle in logical canvas (scene) coordinates, paired with its source hotspot. */
interface HotspotRect {
    x: number;
    y: number;
    width: number;
    height: number;
    hotspot: Hotspot;
}

export class HotspotLayer implements SlideCapability {
    /** Hotspot rects in logical/scene coordinates (raw coords × the slide's hotspot scale). */
    private rects: HotspotRect[] = [];
    private readonly canvas: Canvas;

    constructor(
        private readonly ctx: SlideHostContext,
        private readonly onActivate: (hotspot: Hotspot) => void,
        /** Passed DOWN: the synced set of deactivated hotspot indices (Decision C14). */
        private readonly deactivated: Signal<ReadonlySet<number>>,
        /** Intent UP: request deactivation of the hotspot at this index (host writes it to Y.Doc). */
        private readonly onDeactivate: (index: number) => void,
    ) {
        this.canvas = ctx.canvas;
    }

    /** No freehand/selection tool owns the pointer AND `disableElementUsage` is not set. */
    private isInteractive(): boolean {
        return this.ctx.interaction.activeTool() === null && !this.ctx.interaction.disableElementUsage();
    }

    /**
     * Replace the hotspot set for the shown slide. Keeps only supported types; resets local
     * deactivation. The scale maps raw hotspot image-px into logical/scene coordinates (1 for normal
     * pictures; `720/panorama.height` or `1280/panorama.width` for panorama) — sourced from the host
     * coordinate space (the active background strategy), not re-derived here.
     */
    onSlideChange(slide: IDisplaySlide): void {
        const scale = this.ctx.coordinateSpace().contentToLogicalScale;
        this.rects = (slide.hotspots ?? [])
            .filter(h => SUPPORTED_TYPES.has(h.type))
            .map(h => ({
                x: h.x * scale,
                y: h.y * scale,
                width: h.width * scale,
                height: h.height * scale,
                hotspot: h,
            }));
        // No local reset: the deactivated set is synced display-state, re-armed to empty by the
        // Y.Doc setSlide/removeSlide/resetSlideToInitial cascades (Decision C14).
        this.canvas.requestRenderAll();
    }

    /** Claims the pointer when interactive and over an active hotspot rect (scene coords). */
    claims(pointer: PointerContext): boolean {
        return this.isInteractive() && this.indexOfHit(pointer.scenePoint) >= 0;
    }

    onPointerDown(pointer: PointerContext): void {
        const index = this.indexOfHit(pointer.scenePoint);
        if (index < 0) return;
        const hotspot = this.rects[index].hotspot;
        this.onActivate(hotspot);
        // A non-forward hotspot cannot be re-activated until re-show. Emit the intent UP; the host
        // writes it to Y.Doc and it flows back down through `deactivated` (Decision C14) — the repaint
        // follows the synced-state change, not a local mutation here.
        if (hotspot.type !== HotspotType.forward) {
            this.onDeactivate(index);
        }
    }

    onPointerMove(pointer: PointerContext): void {
        if (!this.isInteractive()) return;
        const over = this.indexOfHit(pointer.scenePoint) >= 0;
        // Restore the canvas's OWN default cursor, not the literal 'default': fabric applies
        // `defaultCursor` BEFORE emitting mouse:move, so writing 'default' here would permanently
        // override the host's grab-to-pan cursor on scrollable slides (panorama with hotspots).
        this.canvas.setCursor(over ? 'pointer' : this.canvas.defaultCursor);
    }

    /** Draw the debug overlay (outline + type + targetSlideId) in scene coordinates. */
    onAfterRender(): void {
        if (!isDebugEnabled() || this.rects.length === 0) return;
        const ctx = this.canvas.getContext();
        const vpt = this.canvas.viewportTransform;
        ctx.save();
        ctx.transform(vpt[0], vpt[1], vpt[2], vpt[3], vpt[4], vpt[5]);
        ctx.lineWidth = 2;
        ctx.font = 'bold 14px monospace';
        ctx.textBaseline = 'top';
        const deactivated = this.deactivated();
        for (let i = 0; i < this.rects.length; i++) {
            const r = this.rects[i];
            const dimmed = deactivated.has(i);
            ctx.strokeStyle = dimmed ? 'rgba(160,160,160,0.7)' : 'rgba(0,150,255,0.9)';
            ctx.fillStyle = dimmed ? 'rgba(160,160,160,0.12)' : 'rgba(0,150,255,0.12)';
            ctx.fillRect(r.x, r.y, r.width, r.height);
            ctx.strokeRect(r.x, r.y, r.width, r.height);
            const label = `${r.hotspot.type}->${r.hotspot.targetSlideId}`;
            ctx.fillStyle = dimmed ? 'rgba(120,120,120,0.9)' : 'rgba(0,90,160,0.95)';
            ctx.fillText(label, r.x + 4, r.y + 4);
        }
        ctx.restore();
    }

    /** The active hotspot at a scene point, or null. */
    hitTest(point: Point): Hotspot | null {
        const i = this.indexOfHit(point);
        return i < 0 ? null : this.rects[i].hotspot;
    }

    private indexOfHit(point: Point): number {
        const deactivated = this.deactivated();
        for (let i = 0; i < this.rects.length; i++) {
            if (deactivated.has(i)) continue;
            const r = this.rects[i];
            if (point.x >= r.x && point.x <= r.x + r.width && point.y >= r.y && point.y <= r.y + r.height) {
                return i;
            }
        }
        return -1;
    }

    dispose(): void {
        this.rects = [];
        // No local deactivation state to clear (it's synced Y.Doc display-state, Decision C14).
    }
}
